// Pure formatting helpers for the Surveys section. No React, no recharts — safe
// to import from both the (eager) list and the (lazy) analytics route.
import type { SurveyStatus } from '@/lib/types';

export function fmtNumber(n: number): string {
  return n.toLocaleString();
}

// `fraction` is 0..1 (completionRate, flaggedRate, OptionCount.percent).
export function fmtPercent(fraction: number): string {
  return `${Math.round(fraction * 100)}%`;
}

// Round a numeric-stat value: integers stay whole, else one decimal.
export function fmtStat(n: number): string {
  return Number.isInteger(n) ? n.toLocaleString() : n.toFixed(1);
}

// Compact "time ago" for a response timestamp; falls back to a date once it's
// more than a week old. `now` is injectable for deterministic tests.
export function fmtRelative(iso: string, now: Date = new Date()): string {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return '—';
  const sec = Math.round((now.getTime() - then.getTime()) / 1000);
  if (sec < 45) return 'just now';
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 7) return `${day}d ago`;
  return then.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: now.getFullYear() === then.getFullYear() ? undefined : 'numeric',
  });
}

export function fmtDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

const COUNTRY_NAME: Record<string, string> = {
  US: 'United States',
  GB: 'United Kingdom',
  CA: 'Canada',
  AU: 'Australia',
  DE: 'Germany',
  FR: 'France',
  TR: 'Türkiye',
  MX: 'Mexico',
  ES: 'Spain',
  IT: 'Italy',
  NL: 'Netherlands',
  IN: 'India',
  BR: 'Brazil',
  JP: 'Japan',
};

export function countryName(code: string | null): string {
  if (!code) return 'Unknown';
  return COUNTRY_NAME[code] ?? code;
}

export const SURVEY_STATUS_META: Record<
  SurveyStatus,
  { label: string; variant: 'success' | 'muted' | 'outline' }
> = {
  draft: { label: 'Draft', variant: 'outline' },
  published: { label: 'Live', variant: 'success' },
  closed: { label: 'Closed', variant: 'muted' },
};
